import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { adminApi, adminAuth, type BlogPostSummary } from '../lib/adminApi';

/** Admin list of all blog posts (drafts + published). */
export function AdminBlogList() {
  const navigate = useNavigate();
  const [items, setItems] = useState<BlogPostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function refresh() {
    setLoading(true);
    adminApi
      .listPosts()
      .then((r) => setItems(r.items))
      .catch((e) => setError(String(e.message)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) return navigate('/admin/login');
    refresh();
  }, [navigate]);

  async function remove(slug: string) {
    if (!confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    await adminApi.deletePost(slug);
    refresh();
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Blog</h1>
          <p className="mt-1 text-sm text-slate-500">All posts – drafts and published.</p>
        </div>
        <Link to="/admin/blog/new" className="btn-primary">
          + New post
        </Link>
      </div>

      {error && <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="mt-6 overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
        {loading && items.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-slate-500">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-slate-500">
            No posts yet. Click "+ New post" to write your first one.
          </div>
        )}
        {items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Updated</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/admin/blog/${p.slug}/edit`} className="block">
                      <div className="font-medium text-slate-900 hover:text-brand-700">
                        {p.title}
                      </div>
                      <div className="mt-0.5 font-mono text-xs text-slate-500">/blog/{p.slug}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    {p.status === 'PUBLISHED' ? (
                      <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 ring-1 ring-inset ring-green-200">
                        Published
                      </span>
                    ) : (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                        Draft
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-xs text-slate-500">
                    {new Date(p.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/blog/${p.slug}/edit`} className="text-xs text-brand-700 hover:underline">
                      Edit
                    </Link>
                    <button
                      onClick={() => remove(p.slug)}
                      className="ml-3 text-xs text-slate-400 hover:text-red-600"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
